import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ClientNameField } from "./fields/ClientNameField";
import { ClientContactField } from "./fields/ClientContactField";
import { useClientsSupabaseStore } from "@/store/api/clientsSupabaseStore";
import { getUserGymId } from "@/utils/gymAuth";
import { useAppName } from "@/hooks/useAppName";
import { useSettingsStore } from "@/store/api/settingsStore";
import { useStateDraft } from "@/hooks/useStateDraft";

interface AddClientFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}


export function AddClientForm({ onSuccess, onCancel }: AddClientFormProps) {
  const { t } = useTranslation(["clients", "common"]);
  const [isLoading, setIsLoading] = useState(false);
  const appInfo = useAppName();
  
  // Get the gym's default bypass cooldown setting
  const defaultBypassCooldown = useSettingsStore(
    state => state.settings.schedule.defaultBypassCooldown
  );

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subscriptionType: "",
    joiningDate: new Date().toISOString().split("T")[0],
    isManuallyActivated: false,
    bypassCooldown: defaultBypassCooldown
  });
  
  // Brouillon persisté : la saisie survit à un reload (mise à jour, kill du
  // process WebView en background). Nettoyé sur succès et sur Annuler.
  const { hasRestoredDraft, clearDraft } = useStateDraft("add-client", formData, setFormData, {
    isEmpty: (d) => !d.firstName && !d.lastName && !d.email && !d.phone,
  });

  // Sync bypassCooldown if settings load after initial mount — mais jamais
  // par-dessus un brouillon restauré (il contient déjà le choix de l'admin).
  useEffect(() => {
    if (hasRestoredDraft) return;
    setFormData(prev => ({
      ...prev,
      bypassCooldown: defaultBypassCooldown
    }));
  }, [defaultBypassCooldown, hasRestoredDraft]);

  const { fetchClients } = useClientsSupabaseStore();
  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };


  const validateForm = () => {
    if (!formData.firstName.trim()) {
      toast.error(t("common:required", { field: t("firstName", { ns: "clients" }) }));
      return false;
    }
    if (!formData.lastName.trim()) {
      toast.error(t("common:required", { field: t("lastName", { ns: "clients" }) }));
      return false;
    }
    if (!formData.email.trim()) {
      toast.error(t("common:required", { field: t("email", { ns: "clients" }) }));
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(t("common:invalidEmail"));
      return false;
    }
 
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const gymId = getUserGymId();
    if (!gymId) {
      toast.error("No gym selected");
      return;
    }

    setIsLoading(true);

    try {
      // Call Edge Function to create client user
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          gym_id: gymId,
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone || '',
          role: 'client',
          bypass_cooldown: formData.bypassCooldown,
          app_name: appInfo
        }
      });

      if (error) throw error;
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create client');
      }

      toast.success(t("clientAddedSuccess", { ns: "clients" }));
      clearDraft();
      fetchClients();
      onSuccess();
    } catch (error) {
      console.error("Error adding client:", error);
      toast.error(error instanceof Error ? error.message : t("common:errorOccurred"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid gap-4 py-4">
      <ClientNameField
        firstName={formData.firstName}
        lastName={formData.lastName}
        onFirstNameChange={(value) => handleChange("firstName", value)}
        onLastNameChange={(value) => handleChange("lastName", value)}
      />

      <ClientContactField
        email={formData.email}
        phone={formData.phone}
        onEmailChange={(value) => handleChange("email", value)}
        onPhoneChange={(value) => handleChange("phone", value)}
      />

      <div className="flex items-center space-x-2">
        <Checkbox
          id="bypassCooldown"
          checked={formData.bypassCooldown}
          onCheckedChange={(checked) => handleChange("bypassCooldown", checked)}
        />
        <Label htmlFor="bypassCooldown" className="text-sm font-medium">
          Autoriser le contournement de la règle des 48h
        </Label>
      </div>

      <div className="flex justify-end space-x-2">
        {/* Annulation EXPLICITE : le brouillon est jeté. Une fermeture par
            overlay/Escape le conserve, volontairement. */}
        <Button
          variant="outline"
          onClick={() => {
            clearDraft();
            onCancel();
          }}
          disabled={isLoading}
        >
          {t("cancel", { ns: "common" })}
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? t("saving", { ns: "common" }) : t("save", { ns: "common" })}
        </Button>
      </div>
    </div>
  );
}
